import { HeroIcon } from '@components/HeroIcon'
import { ScrollBar } from '@components/ScrollBar'
import { useScrollControl } from '@hooks/useScrollControl'
import { IMedia } from '@dtos/Course'
import { useMediaSize } from '@hooks/useMediaSize'
import { Button, View, XStack } from 'tamagui'
import { useMemo, useState, useCallback } from 'react'
import { useMediaRenderWrapperDynamicHeight } from '@hooks/useMediaRenderWrapperDynamicHeight'
import {
  LayoutChangeEvent,
  NativeScrollEvent,
  NativeSyntheticEvent,
} from 'react-native'
import { router, useLocalSearchParams } from 'expo-router'

interface IMediaRenderWrapperProps {
  children: React.ReactNode
  media: IMedia
}

export function MediaRenderWrapper({
  children,
  media,
}: IMediaRenderWrapperProps) {
  const { enrollmentId, courseId, courseContentsId } = useLocalSearchParams()
  const mediaSize = useMediaSize()
  const [contentHeight, setContentHeight] = useState(0)
  const { setIsScrollingContent } = useScrollControl()
  const mediaRenderWrapperHeight = useMediaRenderWrapperDynamicHeight({
    mediaSize,
    mediaType: media.type,
    isExpanded: false,
    textHeight: contentHeight,
    fileExtension: media.fileExt,
  })

  const isAVideo = useMemo(() => {
    return ['Video', 'WebinarHerospark'].includes(media.type ?? '')
  }, [media.type])

  const isSpredSheet = useMemo(() => {
    if (!media.fileExt) return false
    return ['.xls', '.xlsm', '.xlsx', '.csv'].includes(media.fileExt)
  }, [media.fileExt])

  const isFullWidthMedia = useMemo(() => {
    return (
      isAVideo || ['Image', 'Audio'].includes(media.type ?? '') || isSpredSheet
    )
  }, [isAVideo, media.type, isSpredSheet])

  const handleLayout = (event: LayoutChangeEvent) => {
    setContentHeight(event.nativeEvent.layout.height)
  }

  const handleScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const { contentOffset, contentSize, layoutMeasurement } = event.nativeEvent
    const isAtTop = contentOffset.y <= 0
    const isAtBottom =
      contentOffset.y + layoutMeasurement.height >= contentSize.height

    setIsScrollingContent(!isAtTop && !isAtBottom)
  }

  const needScrollbar = media.type === 'Text' && contentHeight > 510
  const isImageMedia = useMemo(() => media.type === 'Image', [media.type])
  const isTextMedia = useMemo(() => media.type === 'Text', [media.type])

  const handleNavigateToImageViewer = useCallback(() => {
    router.navigate(
      `/enrollments/${enrollmentId}/courses/${courseId}/online_course/course_contents/${courseContentsId}/image_viewer`,
    )
  }, [courseContentsId, courseId, enrollmentId])

  return (
    <View
      height={mediaRenderWrapperHeight}
      backgroundColor={isFullWidthMedia ? 'transparent' : '$neutral-100'}
      borderRadius="$border-radius-md"
      maxHeight={mediaRenderWrapperHeight}
      minHeight={mediaRenderWrapperHeight}
      w="100%"
      alignItems={isFullWidthMedia ? 'unset' : 'flex-start'}
      justifyContent="flex-start"
      padding={isFullWidthMedia ? 0 : '$spacing-inset-xxs'}
      overflow="hidden"
      onPressIn={() => setIsScrollingContent(true)}
      onPressOut={() => setIsScrollingContent(false)}
      testID="media-render-wrapper"
      position="relative"
      onPress={() => {
        if (isImageMedia) {
          handleNavigateToImageViewer()
        }
      }}
    >
      {needScrollbar ? (
        <ScrollBar
          scrollBarId="media-render-wrapper"
          displayNativeIndicator={false}
          maxHeight={mediaRenderWrapperHeight - 16}
          onScroll={handleScroll}
        >
          <View onLayout={handleLayout} paddingRight="$spacing-inset-xxs">
            {children}
          </View>
        </ScrollBar>
      ) : (
        <View
          onLayout={handleLayout}
          paddingRight={isTextMedia ? '$spacing-inset-xxs' : 0}
        >
          {children}
        </View>
      )}

      {isImageMedia && (
        <XStack
          position="absolute"
          bottom={16}
          right={16}
          backgroundColor="$background-100"
          opacity={0.9}
          borderRadius="$border-radius-md"
          padding="$spacing-inset-quarck"
          zIndex={1000}
        >
          <Button
            bg="transparent"
            onPress={handleNavigateToImageViewer}
            padding="$spacing-inset-quarck"
            w={30}
            h={30}
            icon={
              <HeroIcon
                icon={['fal', 'expand']}
                size={20}
                color="$neutral-600"
              />
            }
          />
        </XStack>
      )}
    </View>
  )
}
